import React, { useState } from 'react';
import { Eye, EyeOff, ArrowLeft, Mail } from 'lucide-react';
import { authService } from '../services/auth';
import { supabase } from '../config/supabase';

interface LoginProps {
  onLoginSuccess: () => void;
}

export const Login: React.FC<LoginProps> = ({ onLoginSuccess }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isResetMode, setIsResetMode] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    setIsLoading(true);

    try {
      await authService.login(email, password);
      onLoginSuccess();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    setIsLoading(true);

    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });

      if (error) {
        throw error;
      }

      setSuccess('Un email de réinitialisation a été envoyé à votre adresse.');
    } catch (err: any) {
      setError(err.message || 'Erreur lors de l\'envoi de l\'email');
    } finally {
      setIsLoading(false);
    }
  };

  // Mode réinitialisation de mot de passe
  if (isResetMode) {
    return (
      <div className="max-w-2xl mx-auto">
        <button
          onClick={() => {
            setIsResetMode(false);
            setError(null);
            setSuccess(null);
          }}
          className="flex items-center gap-2 text-coral hover:text-coral-dim font-medium mb-4"
        >
          <ArrowLeft className="w-4 h-4" />
          Retour à la connexion
        </button>

        <h2 className="text-2xl font-bold text-ink mb-2 text-center">
          Mot de passe oublié ?
        </h2>
        <p className="text-ink-soft text-center mb-6">
          Entrez votre adresse email pour recevoir un lien de réinitialisation.
        </p>

        <form className="space-y-6" onSubmit={handleResetPassword}>
          <div>
            <label htmlFor="reset-email" className="block text-ink-soft text-xs uppercase tracking-[0.05em] font-semibold mb-1">
              Adresse email
            </label>
            <div className="mt-1 relative">
              <input
                id="reset-email"
                name="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="appearance-none block w-full px-3 py-2 pl-10 bg-cream border border-cream-high rounded-xl text-ink placeholder:text-ink-soft focus:outline-none focus:ring-2 focus:ring-coral/20 focus:border-coral/30"
                placeholder="votre@email.com"
              />
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-ink-soft" />
            </div>
          </div>

          {error && (
            <div className="bg-primary-container text-coral rounded-xl px-4 py-3 text-sm">
              {error}
            </div>
          )}

          {success && (
            <div className="bg-mint-container text-mint rounded-xl px-4 py-3 text-sm">
              {success}
            </div>
          )}

          <button
            type="submit"
            disabled={isLoading}
            className="btn-primary w-full flex justify-center py-3 disabled:opacity-50"
          >
            {isLoading ? 'Envoi en cours...' : 'Envoyer le lien de réinitialisation'}
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto">
      <h2 className="text-2xl font-bold text-ink mb-6 text-center">
        Connexion à votre espace
      </h2>
      <form className="space-y-6" onSubmit={handleSubmit}>
        <div>
          <label htmlFor="email" className="block text-ink-soft text-xs uppercase tracking-[0.05em] font-semibold mb-1">
            Adresse email
          </label>
          <div className="mt-1">
            <input
              id="email"
              name="email"
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="appearance-none block w-full px-3 py-2 bg-cream border border-cream-high rounded-xl text-ink placeholder:text-ink-soft focus:outline-none focus:ring-2 focus:ring-coral/20 focus:border-coral/30"
            />
          </div>
        </div>

        <div>
          <label htmlFor="password" className="block text-ink-soft text-xs uppercase tracking-[0.05em] font-semibold mb-1">
            Mot de passe
          </label>
          <div className="mt-1 relative">
            <input
              id="password"
              name="password"
              type={showPassword ? 'text' : 'password'}
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="appearance-none block w-full px-3 py-2 pr-10 bg-cream border border-cream-high rounded-xl text-ink placeholder:text-ink-soft focus:outline-none focus:ring-2 focus:ring-coral/20 focus:border-coral/30"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute inset-y-0 right-0 flex items-center pr-3 text-ink-soft hover:text-ink"
            >
              {showPassword ? (
                <EyeOff className="w-5 h-5" />
              ) : (
                <Eye className="w-5 h-5" />
              )}
            </button>
          </div>
          <div className="mt-1 text-right">
            <button
              type="button"
              onClick={() => {
                setIsResetMode(true);
                setError(null);
              }}
              className="text-sm text-coral hover:text-coral-dim font-medium"
            >
              Mot de passe oublié ?
            </button>
          </div>
        </div>

        {error && (
          <div className="bg-primary-container text-coral rounded-xl px-4 py-3 text-sm">
            {error}
          </div>
        )}

        <div>
          <button
            type="submit"
            disabled={isLoading}
            className="btn-primary w-full flex justify-center py-3 disabled:opacity-50"
          >
            {isLoading ? 'Connexion...' : 'Se connecter'}
          </button>
        </div>
      </form>
    </div>
  );
};
